import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'reactstrap';
import { StyledModal } from '../StyledModal';
import classNames from 'classnames';

type ChildElement = JSX.Element | string;

interface PropTypes {
  title: string;
  body: ChildElement;
  bodyClassName?: string;
  contentClassName?: string;
  isOpen: boolean;
  confirmText?: string;
  /**
   * Set to true if the 'confirm' button should be disabled right after the user
   * clicks it. Useful to avoid accidental form re-submissions.
   */
  disableAfterConfirm?: boolean;
  isConfirmButtonEnabled?: boolean;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  showCancelButton?: boolean;
  overflowVisible?: boolean;
  size: string;
  footerInfo?: ChildElement;
  external?: React.ReactNode;
  isNegative?: boolean;
}

export function ConfirmationModal(props: PropTypes) {
  const [confirmButtonEnabled, setConfirmButtonEnabled] = useState<boolean>(
    props.isConfirmButtonEnabled !== false,
  );

  function handleConfirmButtonClick() {
    if (props.disableAfterConfirm) setConfirmButtonEnabled(false);

    props.onConfirm();
  }

  useEffect(() => {
    setConfirmButtonEnabled(props.isConfirmButtonEnabled !== false);
  }, [props.isConfirmButtonEnabled]);

  const isNegative = !!props.isNegative;

  return (
    <StyledModal
      onCancel={props.onCancel}
      isOpen={props.isOpen}
      title={props.title}
      overflowVisible={props.overflowVisible}
      size={props.size}
      bodyChildren={props.body}
      bodyClassName={props.bodyClassName}
      contentClassName={props.contentClassName}
      external={props.external}
      footerChildren={
        <Row
          className={classNames(
            'd-flex',
            'w-100',
            'align-items-center',
            'gx-0',
            'mx-0',
          )}
        >
          <Col>{props.footerInfo}</Col>
          <div
            className={classNames('d-flex align-items-center gap-3', {
              'flex-row-reverse': isNegative,
              'justify-content-end': !isNegative,
            })}
          >
            {props.showCancelButton !== false && (
              <Button
                color='danger'
                onClick={isNegative ? handleConfirmButtonClick : props.onCancel}
              >
                {props.cancelText || 'Cancel'}
              </Button>
            )}
            <Button
              color={'primary'}
              disabled={!confirmButtonEnabled}
              onClick={isNegative ? props.onCancel : handleConfirmButtonClick}
            >
              {props.confirmText || 'Confirm'}
            </Button>
          </div>
        </Row>
      }
      footerClassName='px-4'
    />
  );
}
